import { ReactNode } from "react";

import { Icon } from "~/assets/icons/Icon";
import NotificationBlock from "~/components/notificationBlock/NotificationBlock";
import CopyButton from "~/components/ui/copyButton/CopyButton";
import Input from "~/components/ui/input/Input";
import { ALLI_WP_PLUGIN_DOWNLOAD_URL } from "~/helpers/consts";

import FormLabel from "../../login/formLabel/FormLabel";
import styles from "./Wizzard.module.scss";

type WordpressPluginInstallProps = {
  verificationKey: string;
  /** When explicitly `false`, the domain is verified under another account. */
  distinct?: boolean;
  /** Domain name shown in the instructions (e.g. example.com). */
  domainName?: string;
  /** Context-specific footer (e.g. wizard "Done", onboarding "Back/Done"). */
  footer?: ReactNode;
};

/**
 * Presentational WordPress plugin install instructions. Kept free of wizard
 * (WizardContext / route) coupling so it can be reused both inside the
 * Visibility Engine wizard and the site onboarding flow.
 */
export const WordpressPluginInstall = ({
  verificationKey,
  distinct,
  domainName,
  footer,
}: WordpressPluginInstallProps) => {
  return (
    <div>
      <div className={styles.stepContentTitle}>
        Install the AI Visibility Engine WordPress Plugin
      </div>

      {distinct === false && (
        <NotificationBlock type="info" className={styles.verificationAlert}>
          <div>
            <strong>
              This domain is already verified under another account
            </strong>
          </div>
          <div>
            Verification will not succeed while this domain is verified under
            another account. Please remove the verification from the other
            domain first, or contact support for assistance.
          </div>
        </NotificationBlock>
      )}

      <div
        className={`${styles.edgeInstallationDetailsWrapper} ${styles.installWrapper}`}
      >
        <div className={styles.edgeInstallationDetailsContent}>
          <div className={styles.edgeInstallationDetailsStep}>
            <div className={`${styles.stepNumber} ${styles.installStepNumber}`}>
              1
            </div>
            <div className={styles.edgeInstallationDetailsStepContent}>
              <strong>Download and Install WordPress Plugin</strong>
              <div
                className={styles.edgeInstallationDetailsStepContentDescription}
              >
                Install the Visibility Engine plugin on your WordPress site
                {domainName ? (
                  <>
                    {" "}
                    <strong>{domainName}</strong>
                  </>
                ) : null}
                . In your WordPress admin, go to{" "}
                <strong>Plugins → Add New → Upload Plugin</strong>, choose the
                downloaded .zip file, then click <strong>Install Now</strong>{" "}
                and <strong>Activate</strong>.
              </div>
              <div className={styles.stepContentCodeFlex}>
                <a
                  href={ALLI_WP_PLUGIN_DOWNLOAD_URL}
                  className={styles.externalLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  Download Visibility Engine WordPress Plugin
                  <Icon iconName="ExternalLinkIcon" />
                </a>
              </div>
            </div>
          </div>

          <div className={styles.edgeInstallationDetailsStep}>
            <div className={`${styles.stepNumber} ${styles.installStepNumber}`}>
              2
            </div>
            <div className={styles.edgeInstallationDetailsStepContent}>
              <strong>Paste the Verification Key</strong>
              <div
                className={styles.edgeInstallationDetailsStepContentDescription}
              >
                Copy the value below and paste it into the{" "}
                <strong>Verification Key</strong> field on the plugin settings
                page. Click <strong>Install</strong> in the plugin — it will
                publish a file at{" "}
                <code>/.well-known/alli-ai-verification.txt</code>.
              </div>
              <div
                className={`${styles.stepContentCodeFlex} ${styles.verificationKeyField}`}
              >
                <FormLabel label="Verification Key" />
                <div className={styles.stepContentCodeInput}>
                  <Input
                    readOnly
                    value={verificationKey}
                    ownProps={{ noErrors: true }}
                    wrapperClassName={styles.fullWidthInput}
                  />
                  <CopyButton value={verificationKey} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {footer}
    </div>
  );
};

export default WordpressPluginInstall;
